import React, { useContext, useState, useEffect } from 'react'
import { storageWrapper } from '@saatva-bits/pattern-library.utils.storage'
import { getSession } from '@auth0/nextjs-auth0'

import baseRouter from '@/utils/baseRouter'
import { buildServerSideContext, buildServerSideProps } from '@/utils/buildServerSide'
import { OrderContext } from '../store/orderProvider'
import { FormTextInput } from '@saatva-bits/pattern-library.components.form-text-input'
import { Button } from '@saatva-bits/pattern-library.components.button'
import { FormError } from '@saatva-bits/pattern-library.components.form-error'
import { useRouter } from 'next/router'
import { Loader } from '@saatva-bits/pattern-library.components.loader'
import { buildShipments } from '../utils/order'
import AuthButtons from '@/components/AuthButtons'
import { getCartQuantity } from '@/utils/cart'
import { auth0, checkout } from '@/config/globals'

import styles from '../styles/Lookup.module.scss'

export default function OrderLookup ( { loggedUser } ) {
    const { setOrder, isLoading, setLoading, setOrderShipments, errorMsg, setErrorMsg, getCustomerOrder } = useContext(OrderContext)
    const [orderNumber, setOrderNumber] = useState('')
    const [email, setEmail] = useState('')
    const [redirectLoading, setRedirectLoading] = useState(false)
    const [checkoutOrigin, setCheckoutOrigin] = useState(false)
    const router = useRouter()
    const storage = storageWrapper()

    async function formSubmit (e) {
        try {
            e.preventDefault()
            setLoading(true)
            setErrorMsg('')
            const order = await fetchOrderData()
            if (!order) {
                setLoading(false)
                showSubmitError()
            }
        } catch (err) {
            console.error(err)
            setLoading(false)
            showSubmitError()
        }
    }

    const fetchOrderData = async () => {
        const customerOrderJson = await getCustomerOrder(orderNumber, email)
        const order = customerOrderJson?.order
        if (order) {
            setOrder(customerOrderJson.order)
            setOrderShipments(await buildShipments(customerOrderJson.order))
            // Save the email in sessionStorage
            storage.setItem('email', email)
            router.push({
                pathname: '/details',
                query: { orderNumber: orderNumber }
            })
        }

        return order
    }

    const showSubmitError = () => {
        setLoading(false)
        setErrorMsg('Incorrect e-mail address or order number. Please try again or contact us at 1-877-672-2882 for more assistance.')
    }

    useEffect(() => {
        if (router) {
            const { orderNumber } = router.query
            setOrderNumber(orderNumber)
        }
    }, [router])

    useEffect(() => {
        if (loggedUser) {
            window.location.href = auth0.myOrdersUrl
        }
    }, [loggedUser])

    useEffect(() => {
        const trackingData = storage.getItem(checkout.trackingData, 'session')
        const requiredData = trackingData && trackingData.orderNumber && trackingData.email
        
        if (requiredData) {
            setRedirectLoading(true)
            setOrderNumber(trackingData.orderNumber)
            setEmail(trackingData.email)
            setCheckoutOrigin(true)
            storage.removeItem(checkout.trackingData, 'session')
        }
    }, [])

    useEffect(() => {
        if (checkoutOrigin) {
            fetchOrderData()
        }
    }, [checkoutOrigin])

    return (
        <>
            {loggedUser ? (
                <Loader
                    loaderItemCount={4}
                    size="lg"
                    backgroundColor="contrast1"
                    foregroundColor="darkContrast3"
                    className={styles.loggedUserLoader}
                    isFullScreen />
            ) : (
                <div>
                    <form className={styles.lookupForm} onSubmit={formSubmit}>
                        <h2 className={styles.title}>Order Status</h2>
                        <p>Enter the order number and order email address below.</p>
                        <FormTextInput
                            isRequired={true}
                            initialValue={orderNumber}
                            errorMessage='A valid order number is required'
                            inputName='orderNumber'
                            inputType='text'
                            labelText='Order Number'
                            className={styles.textInput}
                            onTextChange={(e) => setOrderNumber(e.currentTarget.value)}
                        />
                        <FormTextInput
                            isRequired={true}
                            initialValue={email}
                            errorMessage='A valid email address is required'
                            inputName='email'
                            inputType='email'
                            labelText='Order Email'
                            className={styles.textInput}
                            onTextChange={(e) => setEmail(e.currentTarget.value)}
                        />
                        <Button
                            className={styles.submitButton}
                            kind='primary'
                            type='submit'
                            disabled={isLoading}
                        >
                            Check Status
                        </Button>
                        {isLoading && <Loader loaderItemCount={4} size="sm" isInline={true} className={styles.loader} />}
                        <FormError id='formError' className={styles.formError} errorMessage={errorMsg} hidden={!errorMsg} />

                        <div className={styles.authContainer}>
                            <AuthButtons />
                        </div>
                    </form>
                    {redirectLoading && <Loader className={styles.redirectLoader} isFullScreen />}
                </div>
            )}
        </>
    )
}

const router = baseRouter().get(async (req, res) => {
    const session = await getSession(req, res)
    const loggedUser = (session && session.user) ? true : false
    
    const meta = {
        destination: `${process.env.NEXT_PUBLIC_STOREFRONT_CORESITE_NODE_DOMAIN}/order-tracker`,
        index: false,
        follow: false,
        title: 'Order Tracker'
    }

    const pageData = {
        title: 'Order Tracker',
        meta,
        cartQuantity: getCartQuantity(req.cookies?.cartInfo),
        loggedUser: loggedUser
    }

    return {
        props: buildServerSideProps(pageData, res.locals)
    }
})

export function getServerSideProps(context) {
    const { req, res } = buildServerSideContext(context)
    return router.run(req, res)
}
